import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { fetchNewsArticleByTitle } from '../contentful';
import { fromUrlFriendly } from '../utils/urlHelpers';
import Button from '../components/Button';
import Breadcrumb from '../components/Breadcrumb';

const NewsArticle = () => {
  const { title } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [article, setArticle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get current section from URL
  const currentSection = location.pathname.startsWith('/industrial') ? 'industrial' : 'household';

  useEffect(() => {
    let isMounted = true;

    const fetchArticle = async () => {
      if (!title) {
        console.log('No title provided in URL');
        if (isMounted) {
          setError('No article title provided');
          setIsLoading(false);
        }
        return;
      }

      try {
        const searchTitle = fromUrlFriendly(title);
        console.log('Fetching article with title:', searchTitle);
        const articleData = await fetchNewsArticleByTitle(searchTitle);

        if (!isMounted) return;

        if (!articleData) {
          console.log('Article not found for title:', searchTitle);
          setError('Article not found');
          setIsLoading(false);
          return;
        }

        console.log('Article found:', articleData);
        setArticle(articleData);
        document.title = articleData.fields.title;
      } catch (err) {
        console.error('Error fetching article:', err);
        if (isMounted) {
          setError('Failed to load article');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchArticle();
    return () => { isMounted = false; };
  }, [title]);

  const handleBackClick = () => {
    navigate(`/${currentSection}/news`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-xl">Loading article...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center">
        <div className="text-xl text-red-600 mb-4">{error}</div>
        <Button
          onClick={handleBackClick}
          className="bg-[#D3242A] text-white hover:bg-[#B31F24]"
        >
          Back to News
        </Button>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center">
        <div className="text-xl text-red-600 mb-4">Article not found</div>
        <Button
          onClick={handleBackClick}
          className="bg-[#D3242A] text-white hover:bg-[#B31F24]"
        >
          Back to News
        </Button>
      </div>
    );
  }

  const {
    id,
    title: articleTitle,
    source,
    date,
    image,
    imageCaption,
    content,
    productImages,
    performanceFeatures,
    productLineup,
    contact,
    tags,
    additionalContent
  } = article.fields;

  const headerImageUrl = image?.fields?.file?.url;

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{articleTitle} | RuggedRed</title>
        <meta name="description" content={articleTitle} />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Breadcrumb 
          items={[
            { label: 'Home', path: '/' },
            { label: 'News', path: `/${currentSection}/news` },
            { label: articleTitle, path: `/${currentSection}/news/${title}` }
          ]} 
        />

        <article className="max-w-4xl mx-auto py-6 sm:py-10">
          {/* Header Image */}
          {headerImageUrl && (
            <div className="mb-8 transform transition-all duration-500 hover:scale-[1.02] max-w-[33.333%] mx-auto">
              <img 
                src={headerImageUrl} 
                alt={articleTitle}
                className="w-full h-auto rounded-lg transition-all duration-500"
              />
              {imageCaption && (
                <p className="text-sm text-gray-600 mt-2 text-center transition-opacity duration-300">{imageCaption}</p>
              )}
            </div>
          )}

          {/* Article Header */}
          <div className="mb-8 transform transition-all duration-500">
            <h1 
              className="text-4xl md:text-5xl font-bold text-[#D3242A] mb-4 transform transition-all duration-500 hover:translate-x-2"
              style={{ 
                fontFamily: "Geogrotesque, sans-serif",
                fontStyle: "italic",
                letterSpacing: "-0.02em"
              }}
            >
              {articleTitle}
            </h1>
            <div className="flex items-center text-gray-600 mb-6 transition-all duration-300">
              <span className="mr-4 transform hover:translate-x-1 transition-transform duration-300">{source}</span>
              <span className="transform hover:translate-x-1 transition-transform duration-300">{date ? new Date(date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }) : ''}</span>
            </div>
          </div>

          {/* Main Content */}
          <div className="max-w-none mb-8">
            {content && (
              <div className="text-gray-700">
                {documentToReactComponents(content, {
                  renderNode: {
                    paragraph: (node, children) => (
                      <p className="mb-6 text-gray-700 leading-relaxed text-lg" style={{ fontFamily: "Montserrat, sans-serif" }}>
                        {children}
                      </p>
                    ),
                    heading1: (node, children) => (
                      <h1 
                        className="text-5xl font-extrabold mb-8 tracking-tight uppercase"
                        style={{ 
                          fontFamily: "Geogrotesque, sans-serif",
                          fontStyle: "italic",
                          letterSpacing: "-0.02em",
                          color: "#D3242A"
                        }}
                      >
                        {children}
                      </h1>
                    ),
                    heading2: (node, children) => (
                      <h2 
                        className="text-4xl font-bold mb-6 tracking-tight uppercase"
                        style={{ 
                          fontFamily: "Geogrotesque, sans-serif",
                          fontStyle: "italic",
                          letterSpacing: "-0.02em",
                          color: "#D3242A"
                        }}
                      >
                        {children}
                      </h2>
                    ),
                    heading3: (node, children) => (
                      <h3 
                        className="text-4xl font-extrabold mb-8 tracking-tight uppercase"
                        style={{ 
                          fontFamily: "Geogrotesque, sans-serif",
                          fontStyle: "italic",
                          letterSpacing: "-0.02em",
                          color: "#D3242A"
                        }}
                      >
                        {children}
                      </h3>
                    ),
                    heading4: (node, children) => (
                      <h4 
                        className="text-2xl font-bold mb-4 tracking-tight uppercase"
                        style={{ 
                          fontFamily: "Geogrotesque, sans-serif",
                          fontStyle: "italic",
                          letterSpacing: "-0.02em",
                          color: "#D3242A"
                        }}
                      >
                        {children}
                      </h4>
                    ),
                    heading5: (node, children) => (
                      <h5 
                        className="text-xl font-bold mb-3 tracking-tight uppercase"
                        style={{ 
                          fontFamily: "Geogrotesque, sans-serif",
                          fontStyle: "italic",
                          letterSpacing: "-0.02em",
                          color: "#D3242A"
                        }}
                      >
                        {children}
                      </h5>
                    ),
                    heading6: (node, children) => (
                      <h6 
                        className="text-lg font-bold mb-2 tracking-tight uppercase"
                        style={{ 
                          fontFamily: "Geogrotesque, sans-serif",
                          fontStyle: "italic",
                          letterSpacing: "-0.02em",
                          color: "#D3242A"
                        }}
                      >
                        {children}
                      </h6>
                    ),
                    listItem: (node, children) => (
                      <li className="leading-relaxed text-lg" style={{ fontFamily: "Montserrat, sans-serif" }}>
                        {children}
                      </li>
                    ),
                    orderedList: (node, children) => (
                      <ol className="list-decimal ml-8 mb-8 space-y-2 text-gray-700">
                        {children}
                      </ol>
                    ),
                    unorderedList: (node, children) => (
                      <ul className="list-disc ml-8 mb-8 space-y-2 text-gray-700 [&>li]:relative [&>li]:pl-2 [&>li]:before:content-['•'] [&>li]:before:absolute [&>li]:before:left-[-1rem] [&>li]:before:text-[#D3242A]">
                        {children}
                      </ul>
                    ),
                    blockquote: (node, children) => (
                      <blockquote className="border-l-4 border-[#D3242A] pl-6 italic my-8 text-gray-700 text-lg" style={{ fontFamily: "Montserrat, sans-serif" }}>
                        {children}
                      </blockquote>
                    ),
                    hyperlink: (node, children) => (
                      <a 
                        href={node.data.uri} 
                        className="text-[#D3242A] hover:underline font-medium"
                        target={node.data.uri.startsWith('http') ? '_blank' : undefined}
                        rel={node.data.uri.startsWith('http') ? 'noopener noreferrer' : undefined}
                        style={{ fontFamily: "Montserrat, sans-serif" }}
                      >
                        {children}
                      </a>
                    ),
                    text: (node) => {
                      let text = node.value;
                      if (node.marks) {
                        node.marks.forEach((mark) => {
                          switch (mark.type) {
                            case 'bold':
                              text = <strong key={mark.type} className="font-bold text-gray-900" style={{ fontFamily: "Montserrat, sans-serif" }}>{text}</strong>;
                              break;
                            case 'italic':
                              text = <em key={mark.type} className="italic" style={{ fontFamily: "Montserrat, sans-serif" }}>{text}</em>;
                              break;
                            case 'underline':
                              text = <u key={mark.type} className="underline" style={{ fontFamily: "Montserrat, sans-serif" }}>{text}</u>;
                              break;
                            case 'code':
                              text = <code key={mark.type} className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono">{text}</code>;
                              break;
                            default:
                              text = <span key={mark.type} style={{ fontFamily: "Montserrat, sans-serif" }}>{text}</span>;
                          }
                        });
                      }
                      return text;
                    },
                    embeddedAssetBlock: (node) => {
                      const { title, description, file } = node.data.target.fields;
                      return (
                        <figure className="my-8">
                          <img
                            src={file.url}
                            alt={description || title}
                            className="w-full h-auto rounded-lg"
                          />
                          {description && (
                            <figcaption className="text-sm text-gray-600 mt-2 text-center" style={{ fontFamily: "Montserrat, sans-serif" }}>
                              {description}
                            </figcaption>
                          )}
                        </figure>
                      );
                    },
                    embeddedEntryBlock: (node) => {
                      const { title, description } = node.data.target.fields;
                      return (
                        <div className="my-8 p-6 bg-gray-50 rounded-lg border border-gray-100">
                          <h3 
                            className="text-xl font-bold text-[#D3242A] mb-2 uppercase"
                            style={{ 
                              fontFamily: "Geogrotesque, sans-serif",
                              fontStyle: "italic",
                              letterSpacing: "-0.02em"
                            }}
                          >
                            {title}
                          </h3>
                          {description && <p className="text-gray-700" style={{ fontFamily: "Montserrat, sans-serif" }}>{description}</p>}
                        </div>
                      );
                    },
                    hr: () => <hr className="my-8 border-gray-200" />,
                    table: (node, children) => (
                      <div className="overflow-x-auto my-8">
                        <table className="min-w-full divide-y divide-gray-200">
                          {children}
                        </table>
                      </div>
                    ),
                    tableRow: (node, children) => (
                      <tr className="divide-x divide-gray-200">
                        {children}
                      </tr>
                    ),
                    tableCell: (node, children) => (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700" style={{ fontFamily: "Montserrat, sans-serif" }}>
                        {children}
                      </td>
                    ),
                    tableHeaderCell: (node, children) => (
                      <th className="px-6 py-4 whitespace-nowrap text-left text-sm font-semibold text-gray-900 bg-gray-50" style={{ fontFamily: "Montserrat, sans-serif" }}>
                        {children}
                      </th>
                    ),
                  },
                })}
              </div>
            )}
          </div>

          {/* Product Images */}
          {productImages && productImages.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              {productImages.map((image, index) => (
                <div key={index} className="flex flex-col items-center transform transition-all duration-500 hover:scale-[1.02]">
                  <div className="w-full aspect-[4/3] flex items-center justify-center">
                    <img 
                      src={image?.fields?.file?.url || ''} 
                      alt={image?.fields?.description || articleTitle} 
                      className="w-full h-full object-contain rounded-lg transition-all duration-500"
                    />
                  </div>
                  <p className="text-sm text-gray-600 mt-2 text-center transition-all duration-300">
                    {image?.fields?.description || ''}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Additional Content */}
          {additionalContent && (
            <div className="mb-8 text-gray-700">
              {documentToReactComponents(additionalContent, {
                renderNode: {
                  paragraph: (node, children) => (
                    <p className="mb-6 text-gray-700 leading-relaxed text-lg" style={{ fontFamily: "Montserrat, sans-serif" }}>
                      {children}
                    </p>
                  ),
                  heading1: (node, children) => (
                    <h1 
                      className="text-5xl font-extrabold mb-8 tracking-tight uppercase"
                      style={{ 
                        fontFamily: "Geogrotesque, sans-serif",
                        fontStyle: "italic",
                        letterSpacing: "-0.02em",
                        color: "#D3242A"
                      }}
                    >
                      {children}
                    </h1>
                  ),
                  heading2: (node, children) => (
                    <h2 
                      className="text-4xl font-bold mb-6 tracking-tight uppercase"
                      style={{ 
                        fontFamily: "Geogrotesque, sans-serif",
                        fontStyle: "italic",
                        letterSpacing: "-0.02em",
                        color: "#D3242A"
                      }}
                    >
                      {children}
                    </h2>
                  ),
                  heading3: (node, children) => (
                    <h3 
                      className="text-4xl font-extrabold mb-8 tracking-tight uppercase"
                      style={{ 
                        fontFamily: "Geogrotesque, sans-serif",
                        fontStyle: "italic",
                        letterSpacing: "-0.02em",
                        color: "#D3242A"
                      }}
                    >
                      {children}
                    </h3>
                  ),
                  heading4: (node, children) => (
                    <h4 
                      className="text-2xl font-bold mb-4 tracking-tight uppercase"
                      style={{ 
                        fontFamily: "Geogrotesque, sans-serif",
                        fontStyle: "italic",
                        letterSpacing: "-0.02em",
                        color: "#D3242A"
                      }}
                    >
                      {children}
                    </h4>
                  ),
                  heading5: (node, children) => (
                    <h5 
                      className="text-xl font-bold mb-3 tracking-tight uppercase"
                      style={{ 
                        fontFamily: "Geogrotesque, sans-serif",
                        fontStyle: "italic",
                        letterSpacing: "-0.02em",
                        color: "#D3242A"
                      }}
                    >
                      {children}
                    </h5>
                  ),
                  heading6: (node, children) => (
                    <h6 
                      className="text-lg font-bold mb-2 tracking-tight uppercase"
                      style={{ 
                        fontFamily: "Geogrotesque, sans-serif",
                        fontStyle: "italic",
                        letterSpacing: "-0.02em",
                        color: "#D3242A"
                      }}
                    >
                      {children}
                    </h6>
                  ),
                  listItem: (node, children) => (
                    <li className="leading-relaxed text-lg" style={{ fontFamily: "Montserrat, sans-serif" }}>
                      {children}
                    </li>
                  ),
                  orderedList: (node, children) => (
                    <ol className="list-decimal ml-8 mb-8 space-y-2 text-gray-700">
                      {children}
                    </ol>
                  ),
                  unorderedList: (node, children) => (
                    <ul className="list-disc ml-8 mb-8 space-y-2 text-gray-700 [&>li]:relative [&>li]:pl-2 [&>li]:before:content-['•'] [&>li]:before:absolute [&>li]:before:left-[-1rem] [&>li]:before:text-[#D3242A]">
                      {children}
                    </ul>
                  ),
                  blockquote: (node, children) => (
                    <blockquote className="border-l-4 border-[#D3242A] pl-6 italic my-8 text-gray-700 text-lg" style={{ fontFamily: "Montserrat, sans-serif" }}>
                      {children}
                    </blockquote>
                  ),
                  hyperlink: (node, children) => (
                    <a 
                      href={node.data.uri} 
                      className="text-[#D3242A] hover:underline font-medium"
                      target={node.data.uri.startsWith('http') ? '_blank' : undefined}
                      rel={node.data.uri.startsWith('http') ? 'noopener noreferrer' : undefined}
                      style={{ fontFamily: "Montserrat, sans-serif" }}
                    >
                      {children}
                    </a>
                  ),
                  text: (node) => {
                    let text = node.value;
                    if (node.marks) {
                      node.marks.forEach((mark) => {
                        switch (mark.type) {
                          case 'bold':
                            text = <strong key={mark.type} className="font-bold text-gray-900" style={{ fontFamily: "Montserrat, sans-serif" }}>{text}</strong>;
                            break;
                          case 'italic':
                            text = <em key={mark.type} className="italic" style={{ fontFamily: "Montserrat, sans-serif" }}>{text}</em>;
                            break;
                          case 'underline':
                            text = <u key={mark.type} className="underline" style={{ fontFamily: "Montserrat, sans-serif" }}>{text}</u>;
                            break;
                          case 'code':
                            text = <code key={mark.type} className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono">{text}</code>;
                            break;
                          default:
                            text = <span key={mark.type} style={{ fontFamily: "Montserrat, sans-serif" }}>{text}</span>;
                        }
                      });
                    }
                    return text;
                  },
                  embeddedAssetBlock: (node) => {
                    const { title, description, file } = node.data.target.fields;
                    return (
                      <figure className="my-8">
                        <img
                          src={file.url}
                          alt={description || title}
                          className="w-full h-auto rounded-lg"
                        />
                        {description && (
                          <figcaption className="text-sm text-gray-600 mt-2 text-center" style={{ fontFamily: "Montserrat, sans-serif" }}>
                            {description}
                          </figcaption>
                        )}
                      </figure>
                    );
                  },
                  embeddedEntryBlock: (node) => {
                    const { title, description } = node.data.target.fields;
                    return (
                      <div className="my-8 p-6 bg-gray-50 rounded-lg border border-gray-100">
                        <h3 
                          className="text-xl font-bold text-[#D3242A] mb-2 uppercase"
                          style={{ 
                            fontFamily: "Geogrotesque, sans-serif",
                            fontStyle: "italic",
                            letterSpacing: "-0.02em"
                          }}
                        >
                          {title}
                        </h3>
                        {description && <p className="text-gray-700" style={{ fontFamily: "Montserrat, sans-serif" }}>{description}</p>}
                      </div>
                    );
                  },
                  hr: () => <hr className="my-8 border-gray-200" />,
                  table: (node, children) => (
                    <div className="overflow-x-auto my-8">
                      <table className="min-w-full divide-y divide-gray-200">
                        {children}
                      </table>
                    </div>
                  ),
                  tableRow: (node, children) => (
                    <tr className="divide-x divide-gray-200">
                      {children}
                    </tr>
                  ),
                  tableCell: (node, children) => (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700" style={{ fontFamily: "Montserrat, sans-serif" }}>
                      {children}
                    </td>
                  ),
                  tableHeaderCell: (node, children) => (
                    <th className="px-6 py-4 whitespace-nowrap text-left text-sm font-semibold text-gray-900 bg-gray-50" style={{ fontFamily: "Montserrat, sans-serif" }}>
                      {children}
                    </th>
                  ),
                },
              })}
            </div>
          )}

          {/* Performance Features */}
          {performanceFeatures && performanceFeatures.length > 0 && (
            <div className="mb-12 transform transition-all duration-500">
              <h2 
                className="text-3xl font-bold text-[#D3242A] mb-6 transform transition-all duration-500 hover:translate-x-2"
                style={{ 
                  fontFamily: "Geogrotesque, sans-serif",
                  fontStyle: "italic",
                  letterSpacing: "-0.02em"
                }}
              >
                Performance Features
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {performanceFeatures.map((feature, index) => (
                  <div 
                    key={index} 
                    className="bg-white p-6 rounded-lg border border-gray-100 transform transition-all duration-500 hover:translate-x-2 hover:shadow-md"
                  >
                    <h3 
                      className="text-xl font-bold text-[#D3242A] mb-3 transform transition-all duration-300"
                      style={{ 
                        fontFamily: "Geogrotesque, sans-serif",
                        fontStyle: "italic",
                        letterSpacing: "-0.02em"
                      }}
                    >
                      {feature.title}
                    </h3>
                    <p className="text-gray-700 leading-relaxed transition-all duration-300">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Product Lineup */}
          {productLineup && productLineup.length > 0 && (
            <div className="mb-12 transform transition-all duration-500">
              <h2 
                className="text-3xl font-bold text-[#D3242A] mb-6 transform transition-all duration-500 hover:translate-x-2"
                style={{ 
                  fontFamily: "Geogrotesque, sans-serif",
                  fontStyle: "italic",
                  letterSpacing: "-0.02em"
                }}
              >
                Product Lineup
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {productLineup.map((product, index) => (
                  <div 
                    key={index} 
                    className="bg-white p-6 rounded-lg border border-gray-100 transform transition-all duration-500 hover:translate-x-2 hover:shadow-md"
                  >
                    <h3 
                      className="text-xl font-bold text-[#D3242A] mb-3 transform transition-all duration-300"
                      style={{ 
                        fontFamily: "Geogrotesque, sans-serif",
                        fontStyle: "italic",
                        letterSpacing: "-0.02em"
                      }}
                    >
                      {product.name}
                    </h3>
                    <p className="text-gray-700 leading-relaxed transition-all duration-300">{product.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Media Contact */}
          {contact && (
            <div className="bg-white p-8 rounded-lg border border-gray-100 mb-12 transform transition-all duration-500 hover:translate-x-2 hover:shadow-md">
              <h3 
                className="text-2xl font-bold text-[#D3242A] mb-6 transform transition-all duration-300"
                style={{ 
                  fontFamily: "Geogrotesque, sans-serif",
                  fontStyle: "italic",
                  letterSpacing: "-0.02em"
                }}
              >
                Media Contact
              </h3>
              <div className="space-y-3">
                {contact.name && <p className="text-lg font-semibold text-gray-900 transform transition-all duration-300 hover:translate-x-2">{contact.name}</p>}
                {contact.title && <p className="text-gray-700 transform transition-all duration-300 hover:translate-x-2">{contact.title}</p>}
                {contact.company && <p className="text-gray-700 transform transition-all duration-300 hover:translate-x-2">{contact.company}</p>}
                {contact.phone && <p className="text-gray-700 transform transition-all duration-300 hover:translate-x-2">Phone: {contact.phone}</p>}
                {contact.email && (
                  <p className="text-gray-700 transform transition-all duration-300 hover:translate-x-2">
                    Email: <a href={`mailto:${contact.email}`} className="text-[#D3242A] hover:underline transition-all duration-300">{contact.email}</a>
                  </p>
                )}
                {contact.website && (
                  <p className="text-gray-700 transform transition-all duration-300 hover:translate-x-2">
                    Website: <a href={`https://${contact.website}`} target="_blank" rel="noopener noreferrer" className="text-[#D3242A] hover:underline transition-all duration-300">{contact.website}</a>
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Tags */}
          {tags && tags.length > 0 && (
            <div className="mb-12">
              <div className="flex flex-wrap gap-3">
                {tags.map((tag, index) => (
                  <span 
                    key={index}
                    className="px-4 py-2 bg-[#D3242A] text-white rounded-full text-sm font-medium transform transition-all duration-300 hover:scale-110 hover:bg-[#B31F24]"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </article>
      </div>
    </div>
  );
};

export default NewsArticle; 